<template>
  <q-modal show wide dismissable @close="$emit('close')">
    <template #header>
      <div class="flex items-center">
        <span class="text-gray-90 text-lg font-medium">{{ $t('flag_manager.title') }}</span>
        <span class="ml-6">
          <ui-tag
            v-for="(_, tag) of tagFilter"
            :key="tag"
            v-tippy="tagTooltips[tag]"
            :class="[!tagFilter[tag] && 'line-through opacity-50', 'mx-1 cursor-pointer']"
            :theme="tagColors[tag]"
            @click="tagFilter[tag] = !tagFilter[tag]"
          >
            {{ tag }}
          </ui-tag>
        </span>
      </div>
    </template>

    <div class="min-h-[350px] columns-2 gap-x-12">
      <div v-for="feat in filteredFlags" :key="feat.name" class="mb-4 flex w-full break-inside-avoid-column">
        <ui-pill-toggle
          v-tippy="{ content: 'localValue' in feat && 'Locally overidden', arrow: false, offset: [0, 5] }"
          :class="'localValue' in feat && '[&_input+span]:!bg-yellow-40'"
          class="mr-3"
          :model-value="'localValue' in feat ? feat.localValue : feat.value"
          @update:model-value="setOverride(feat.name, $event)"
        />
        <div class="flex flex-1 justify-between gap-1 text-sm font-medium">
          <div class="flex-1">
            <span v-if="feat.accountTooltip" v-tippy="{ content: feat.accountTooltip, maxWidth: 500 }">🏢 </span>
            <span v-tippy="feat.description">{{ feat.name }}</span>
          </div>
          <ui-tag v-for="t in feat.tags" :key="t.value" v-tippy="t.tooltip" :theme="tagColors[t.value]">
            {{ t.value }}
          </ui-tag>
        </div>
      </div>
    </div>

    <template #actions>
      <ui-button secondary class="mr-3" @click="$emit('close')">
        {{ $t('universal.cancel') }}
      </ui-button>
      <ui-button :disabled="!hasChanges" @click="save()">{{ $t('flag_manager.save') }}</ui-button>
    </template>
  </q-modal>
</template>

<script lang="ts" setup>
import { computed, ref, onBeforeMount } from 'vue'
import { UiPillToggle, UiTag, UiButton } from '@klausapp/ui-kit'
import type { UiTagThemeType } from '@klausapp/ui-kit/types'
import dayjs from 'dayjs'

import { oldApi } from '@/api'
import QModal from '@/components/QModal.vue'
import { writeOverrides } from '@/utils/feature-flag-override'
import { session } from '@/composables/useSession'
import type { Features } from '@/types/session'

defineEmits<{ (e: 'close'): void }>()

type Tags = 'plan' | 'addon' | 'data' | 'dev' | 'custom'

interface FeatureFlag {
  id: string
  name: keyof Features
  value: boolean
  description: string
  tags: { value: Tags; tooltip: string }[]
  accountTooltip: string | null
  localValue?: boolean
}

const tagColors: Record<Tags, UiTagThemeType> = {
  plan: 'brand',
  addon: 'blue',
  data: 'green',
  dev: 'yellow',
  custom: 'default',
}

const tagTooltips = {
  plan: 'Included with pricing plan',
  addon: 'Included with addon',
  data: 'This feature requires data analysis',
  dev: 'This feature is in development',
  custom: 'Custom development for a client',
}

const tagFilter = ref<Record<Tags, boolean>>({ dev: true, plan: false, addon: false, data: false, custom: false })
const hiddenTags = computed(() => (Object.keys(tagFilter.value) as Tags[]).filter((k) => !tagFilter.value[k]))

const flags = ref<FeatureFlag[]>([])

const filteredFlags = computed(() =>
  flags.value.filter((f) => {
    const visibleTags = f.tags.filter((t) => !hiddenTags.value.includes(t.value))
    return 'localValue' in f || visibleTags.length > 0
  }),
)

const fetchFlags = async () => {
  const { data } = await oldApi.get(`office/account-feature-flags/${session.account.id}`).json<{ data: any }>()
  flags.value = data
    .sort((a: any, b: any) => a.name.localeCompare(b.name))
    .map(({ accountOverride: o, tag, ...f }: any) => {
      let accountTooltip: string | null = null
      if (o) {
        const action = f.value ? 'Enabled' : 'Disabled'
        const user = (o.updatedBy || o.createdBy)?.name
        const on = dayjs(o.created).format('YYYY-MM-DD')
        accountTooltip = `${action} for account by ${user}: "${o.reason}" on ${on}`
      }

      const tags = (tag || '').split(',').map((value: Tags) => {
        let tooltip = tagTooltips[value]
        if (value === 'plan' && f.inPlan) tooltip = `Included in plan '${f.inPlan}'`
        if (value === 'addon' && f.inAddon) tooltip = `Included in addon '${f.inAddon}'`
        return { value, tooltip }
      })

      const res = { ...f, tags, accountTooltip } as FeatureFlag
      const flagName = f.name as keyof Features
      if (session.features[flagName] !== f.value) res.localValue = session.features[flagName]
      return res
    })
}

const setOverride = (name: string, value: boolean) => {
  const flag = flags.value.find((f) => f.name === name)
  if (!flag) throw new Error('invalid flag name')
  if (flag.value === value) {
    delete flag.localValue
  } else {
    flag.localValue = value
  }
}

const hasChanges = computed(
  () => flags.value.filter((f) => ('localValue' in f ? f.localValue : f.value) != session.features[f?.name]).length > 0,
)

const save = () => {
  const overrides = Object.fromEntries(flags.value.filter((f) => 'localValue' in f).map((f) => [f.name, f.localValue]))
  writeOverrides(overrides)
  location.reload()
}

onBeforeMount(() => fetchFlags())
</script>
